import type { Holiday, DeadlineDayType, HolidayScope } from '@/types/deadlines';
import { getHolidays, isBusinessDay, getHolidayScopesForCCAA } from './holidayService';

/**
 * Añade días hábiles a una fecha, excluyendo festivos y fines de semana
 */
export function addBusinessDays(
  startDate: Date,
  days: number,
  holidays: Holiday[]
): Date {
  const result = new Date(startDate);
  let addedDays = 0;

  while (addedDays < days) {
    result.setDate(result.getDate() + 1);
    if (isBusinessDay(result, holidays)) {
      addedDays++;
    }
  }

  return result;
}

/**
 * Añade días naturales a una fecha
 */
export function addNaturalDays(startDate: Date, days: number): Date {
  const result = new Date(startDate);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Añade meses a una fecha
 */
export function addMonths(startDate: Date, months: number): Date {
  const result = new Date(startDate);
  result.setMonth(result.getMonth() + months);
  return result;
}

/**
 * Si la fecha cae en festivo o fin de semana, obtiene el siguiente día hábil
 */
export function getNextBusinessDay(date: Date, holidays: Holiday[]): Date {
  const result = new Date(date);
  
  while (!isBusinessDay(result, holidays)) {
    result.setDate(result.getDate() + 1);
  }
  
  return result;
}

/**
 * Calcula la fecha límite según el tipo de plazo
 */
export async function calculateDueDate(
  startDate: Date,
  days: number,
  dayType: DeadlineDayType,
  autonomousCommunity: string = 'otra',
  organizationId?: string
): Promise<{ dueDate: Date; originalDueDate: Date }> {
  // Obtener festivos aplicables
  const scopes = getHolidayScopesForCCAA(autonomousCommunity);
  
  // Calcular años que necesitamos
  const estimatedEndDate = new Date(startDate);
  estimatedEndDate.setDate(estimatedEndDate.getDate() + days + 60); // margen de 60 días extra
  
  const startYear = startDate.getFullYear();
  const endYear = estimatedEndDate.getFullYear();
  
  let holidays: Holiday[] = [];
  for (let year = startYear; year <= endYear; year++) {
    const yearHolidays = await getHolidays(year, scopes, organizationId);
    holidays = [...holidays, ...yearHolidays];
  }

  let originalDueDate: Date;
  let dueDate: Date;

  switch (dayType) {
    case 'habiles':
      originalDueDate = addBusinessDays(startDate, days, holidays);
      dueDate = originalDueDate; // Ya está ajustado
      break;
    
    case 'naturales':
      originalDueDate = addNaturalDays(startDate, days);
      // Si cae en no hábil, siguiente hábil
      dueDate = getNextBusinessDay(originalDueDate, holidays);
      break;
    
    case 'fecha_fija':
    default:
      originalDueDate = new Date(startDate);
      dueDate = getNextBusinessDay(originalDueDate, holidays);
      break;
  }

  return { dueDate, originalDueDate };
}

/**
 * Calcula la fecha límite para un plazo dado en formato texto
 * Ej: "1 mes", "15 días hábiles", "30 días naturales"
 */
export function parsePlazoText(text: string): { days: number; dayType: DeadlineDayType; isMonths: boolean } | null {
  const patterns = [
    // "1 mes", "2 meses"
    /(\d+)\s*mes(?:es)?/i,
    // "15 días hábiles", "30 días naturales", "10 días"
    /(\d+)\s*días?\s*(hábiles?|naturales?)?/i,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const value = parseInt(match[1], 10);
      
      if (pattern.source.includes('mes')) {
        // Convertir meses a días (aproximado: 30 días por mes para días hábiles es ~22)
        return {
          days: value * 22, // Aproximación de días hábiles por mes
          dayType: 'habiles',
          isMonths: true,
        };
      }
      
      const typeText = match[2]?.toLowerCase() || '';
      let dayType: DeadlineDayType = 'habiles'; // Por defecto en administración son hábiles
      
      if (typeText.includes('natural')) {
        dayType = 'naturales';
      }
      
      return { days: value, dayType, isMonths: false };
    }
  }

  return null;
}

/**
 * Calcula los días restantes hasta una fecha
 */
export function getDaysUntil(dueDate: Date): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  
  const diffTime = due.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

/**
 * Obtiene el color de urgencia según los días restantes
 */
export function getUrgencyLevel(daysUntil: number, status: string): 'vencido' | 'hoy' | 'urgente' | 'proximo' | 'normal' | 'completado' {
  if (status === 'completado') return 'completado';
  if (status === 'vencido' || daysUntil < 0) return 'vencido';
  if (daysUntil === 0) return 'hoy';
  if (daysUntil <= 3) return 'urgente';
  if (daysUntil <= 7) return 'proximo';
  return 'normal';
}

/**
 * Formatea la fecha límite en texto amigable
 */
export function formatDueDate(dueDate: Date | string): string {
  const date = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
  const daysUntil = getDaysUntil(date);
  
  if (daysUntil < 0) {
    return `Vencido hace ${Math.abs(daysUntil)} días`;
  }
  if (daysUntil === 0) {
    return 'Vence hoy';
  }
  if (daysUntil === 1) {
    return 'Vence mañana';
  }
  if (daysUntil <= 7) {
    return `Vence en ${daysUntil} días`;
  }
  
  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Obtiene la fecha fija anual a partir de un string MM-DD
 */
export function getFixedDateForYear(fixedDate: string, year: number): Date {
  const [month, day] = fixedDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Calcula la próxima ocurrencia de una fecha fija anual
 */
export function getNextFixedDate(fixedDate: string): Date {
  const today = new Date();
  const thisYearDate = getFixedDateForYear(fixedDate, today.getFullYear());
  
  if (thisYearDate >= today) {
    return thisYearDate;
  }
  
  return getFixedDateForYear(fixedDate, today.getFullYear() + 1);
}
